import React, { useEffect, useState, useContext } from "react";
import { Row, Col, Typography, Button, Empty, Drawer, Dropdown } from "antd";
import ShopCardSkeleton from "../component/skeletons/ShopCardSkeleton";
import Navbar from "./Navbar";
import { useNavigate, useLocation } from "react-router-dom";
import ShopServ from "../service/ShopServ";
import CategoryServ from "../service/CategoryServ";
import BrandsServ from "../service/BrandsServ";
import RegularProductListing from "../component/RegularProductListing";
import Footer from "../component/Footer";
import { LocationContext } from "../LocationContext";
import SafeImage from "../component/SafeImage";
import { useDeliveryEstimate } from "../hooks/useDeliveryEstimate";
import { IconTruck, IconAppWindow, IconList, IconBox, IconBuildingStore, IconFilterOff, IconChevronDown } from "@tabler/icons-react";
import GlobalTooltip from "../component/GlobalTooltip";
import useFilters from "../hooks/useFilters";
import GlobalFilterSidebar from "../component/GlobalFilterSidebar";
import "../css/shopPage.css";

import { capitalizeFirst, renderSafeText, findMatchingCategoryIds } from "../util/unitFormatter";
import dayjs from "dayjs";
const { Title, Text } = Typography;

const formatTimeStr = (timeStr) => {
    if (!timeStr) return '';
    // If it's a full ISO date (closedUntil), parse it with dayjs
    if (timeStr.includes('T') || timeStr.length > 15) {
        return dayjs(timeStr).format('MMM DD, hh:mm A');
    }
    const parts = timeStr.split(':');
    if (parts.length < 2) return timeStr;
    let h = parseInt(parts[0], 10);
    let m = parts[1].substring(0, 2); // get just the minutes part, ignore seconds or AM/PM string attached
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    h = h ? h : 12;
    return `${h.toString().padStart(2, '0')}:${m} ${ampm}`;
};

const getRelativeCloseText = (closedUntil) => {
    const now = dayjs();
    const reopen = dayjs(closedUntil);
    const diffHours = reopen.diff(now, 'hour', true);

    if (diffHours <= 0) return "soon";
    if (diffHours <= 2.5) return "in 2 Hours";
    if (diffHours <= 4.5) return "in 4 Hours";

    const isTomorrow = reopen.isSame(now.add(1, 'day'), 'day');
    if (isTomorrow || diffHours <= 24) return "Tomorrow Morning";

    const diffDays = Math.ceil(reopen.diff(now, 'day', true));
    return `After ${diffDays} Days`;
};

const SearchResults = () => {
    const [shops, setShops] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalProducts, setTotalProducts] = useState(0);
    const initialSearchParams = new URLSearchParams(useLocation().search);
    const initialSearchQuery = initialSearchParams.get("search") || "";
    const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
    const [categories, setCategories] = useState([]);
    const [activeTab, setActiveTab] = useState('all');
    const [brands, setBrands] = useState([]);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [shopStatuses, setShopStatuses] = useState({});
    const [viewType, setViewType] = useState('grid');
    const navigate = useNavigate();
    const location = useLocation();

    // Using LocationContext for potential future geolocation filtering if needed
    // But primary driver is the search query
    const { latitude, longitude, radius } = useContext(LocationContext);

    const { filters, debouncedFilters, updateFilter, resetFilters: resetFilterState } = useFilters({
        search: initialSearchQuery,
        categories: [],
        brands: [],
        rating: 0,
        sort: 'relevance',
    });

    const shopIds = React.useMemo(() => shops.map(s => s.id), [shops]);
    const { formatETA } = useDeliveryEstimate(shopIds, latitude, longitude);

    const filtersOnOpenRef = React.useRef(null);

    // Capture filter state when drawer opens to distinguish between just looking vs actively searching
    useEffect(() => {
        if (mobileFiltersOpen) {
            filtersOnOpenRef.current = JSON.stringify(debouncedFilters);
        } else {
            filtersOnOpenRef.current = null;
        }
    }, [mobileFiltersOpen]);

    useEffect(() => {
        const handleShopStatusUpdate = (e) => {
            const data = e.detail;
            if (data?.shopId) {
                setShopStatuses(prev => ({
                    ...prev,
                    [data.shopId]: data.availability
                }));
            }
        };
        window.addEventListener('shopStatusUpdated', handleShopStatusUpdate);
        return () => window.removeEventListener('shopStatusUpdated', handleShopStatusUpdate);
    }, []);



    useEffect(() => {
        const handleToggleFilter = () => setMobileFiltersOpen(true);
        window.addEventListener('toggle-global-filter', handleToggleFilter);
        return () => window.removeEventListener('toggle-global-filter', handleToggleFilter);
    }, []);

    useEffect(() => {
        fetchFilterOptions();
    }, []);

    const fetchFilterOptions = async () => {
        try {
            const [catRes, brandRes] = await Promise.all([
                CategoryServ.index({ limit: "all" }),
                BrandsServ.index({ limit: "all" })
            ]);
            setCategories(catRes.data || []);
            setBrands(brandRes.data || []);
        } catch (err) {
            console.error("Error fetching filter options:", err);
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const query = params.get("search");
        if (query) {
            setSearchQuery(query);
            updateFilter('search', query);
            fetchShops(query);
        } else {
            navigate("/");
        }
    }, [location.search, navigate]);

    useEffect(() => {
        let isCurrent = true;
        if (searchQuery) {
            fetchShops(searchQuery, isCurrent);
        }
        return () => { isCurrent = false; };
    }, [JSON.stringify(debouncedFilters), latitude, longitude, radius, categories]);

    const fetchShops = async (query, isCurrentFlag = true) => {
        setLoading(true);
        try {
            const searchQueryTerm = debouncedFilters.search || query;
            const params = { search: searchQueryTerm };

            if (latitude && longitude) {
                params.lat = latitude;
                params.lng = longitude;
                params.radius = radius;
            }

            if (debouncedFilters.categories.length > 0) params.categories = debouncedFilters.categories.join(',');
            if (debouncedFilters.brands.length > 0) params.brands = debouncedFilters.brands.join(',');
            if (debouncedFilters.rating > 0) params.rating = debouncedFilters.rating;

            // Fetch shops by text search
            const textPromise = ShopServ.index(params);

            // Also find matching category IDs if no explicit categories selected
            let catPromise = null;
            if (searchQueryTerm && debouncedFilters.categories.length === 0 && categories.length > 0) {
                const matchedCatIds = findMatchingCategoryIds(searchQueryTerm, categories);
                if (matchedCatIds.length > 0) {
                    const catParams = { ...params, categories: matchedCatIds.join(',') };
                    delete catParams.search;
                    catPromise = ShopServ.index(catParams);
                }
            }

            const [resText, resCat] = await Promise.all([textPromise, catPromise ? catPromise : Promise.resolve(null)]);

            const shopsText = resText?.data || [];
            const shopsCat = resCat?.data || [];

            const combinedRaw = [...shopsText, ...shopsCat];
            const seenIds = new Set();
            const shopsData = [];
            combinedRaw.forEach(shop => {
                const sId = shop?._id || shop?.id;
                if (sId && !seenIds.has(sId)) {
                    seenIds.add(sId);
                    shopsData.push(shop);
                }
            });

            let formatted = shopsData.map((shop) => ({
                id: shop?._id || shop?.id,
                ...shop,
                name: shop?.name || "Unnamed Shop",
                description: shop?.description || "No description available",
                imageUrl: shop?.imageUrl || "https://via.placeholder.com/200?text=No+Image",
                rating: shop?.rating || Math.floor(Math.random() * 2) + 3.5,
            }));

            if (debouncedFilters.rating > 0) {
                formatted = formatted.filter(shop => (shop.rating || 0) >= debouncedFilters.rating);
            }

            if (debouncedFilters.sort) {
                const [field, order] = debouncedFilters.sort.split('-');
                formatted.sort((a, b) => {
                    if (field === 'name') {
                        return order === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
                    }
                    return 0;
                });
            }

            if (isCurrentFlag) {
                setShops(formatted);
            }
        } catch (err) {
            console.error("Error fetching shops for search:", err);
            if (isCurrentFlag) {
                setShops([]);
            }
        } finally {
            if (isCurrentFlag) {
                setLoading(false);
            }
        }
    };

    const handleReset = () => {
        resetFilterState();
        setMobileFiltersOpen(false);
    };

    const isSearchEmpty = !loading && shops.length === 0 && totalProducts === 0;

    if (!searchQuery) return null;

    if (isSearchEmpty) {
        return (
            <>
                <Navbar />
                <div className="standard-page-container search-empty-container">
                    <div className="premium-empty-card">
                        <div className="premium-empty-icon-wrapper">
                            <IconFilterOff size={36} className="premium-empty-icon" />
                        </div>

                        <Title level={2} className="premium-empty-title">
                            No Results Found for "{searchQuery}"
                        </Title>

                        <div className="premium-empty-actions">
                            <Button
                                type="primary"
                                onClick={handleReset}
                                className="premium-empty-btn-primary"
                            >
                                Clear All Filters
                            </Button>
                            <Button
                                onClick={() => navigate("/")}
                                className="premium-empty-btn-secondary"
                            >
                                Go Back Home
                            </Button>
                        </div>

                        <div className="premium-empty-suggestions">
                            <Text className="suggestions-label">Popular Searches:</Text>
                            <div className="suggestions-tags">
                                {["Groceries", "Fruits", "Electronics", "Mobiles", "Fashion", "Beauty"].map(tag => (
                                    <span
                                        key={tag}
                                        className="suggestion-tag"
                                        onClick={() => {
                                            setSearchQuery(tag);
                                            updateFilter('search', tag);
                                            navigate(`/search?search=${tag}`);
                                        }}
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Navbar />
            <div className="standard-page-container">
                <div>
                    <div className="pcu-flex pcu-justify-between pcu-flex-center-y pcu-margin-bottom-24">
                        <div>
                            <Title level={2} className="pcu-margin-bottom-8" style={{ fontSize: '28px', fontWeight: 800 }}>
                                Search Results for "{searchQuery}"
                            </Title>
                            <Text type="secondary" style={{ fontSize: '14px', display: 'block' }}>
                                Showing results for {searchQuery}
                            </Text>
                        </div>
                        <div className="pcu-flex pcu-bg-white pcu-border" style={{ borderRadius: '12px', padding: '4px', border: '1px solid #e2e8f0', gap: '4px' }}>
                            <div
                                onClick={() => setViewType('grid')}
                                style={{ padding: '8px', background: viewType === 'grid' ? '#f1f5f9' : 'transparent', borderRadius: '8px', cursor: 'pointer', color: viewType === 'grid' ? 'var(--nearzon-purple)' : '#94a3b8', display: 'flex' }}
                            >
                                <IconAppWindow size={20} />
                            </div>
                            <div
                                onClick={() => setViewType('list')}
                                style={{ padding: '8px', background: viewType === 'list' ? '#f1f5f9' : 'transparent', borderRadius: '8px', cursor: 'pointer', color: viewType === 'list' ? 'var(--nearzon-purple)' : '#94a3b8', display: 'flex' }}
                            >
                                <IconList size={20} />
                            </div>
                        </div>
                    </div>

                    {/* Toolbar */}
                    <div className="sr-toolbar">
                        <div className="sr-segmented pcu-flex pcu-gap-4">
                            <Button
                                type={activeTab === 'all' ? "primary" : "text"}
                                onClick={() => setActiveTab('all')}
                                style={{ background: activeTab === 'all' ? 'var(--nearzon-purple)' : 'transparent', color: activeTab === 'all' ? '#fff' : '#64748b', borderRadius: '6px', fontWeight: 600, border: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                                <IconAppWindow size={16} /> All ({totalProducts + shops.length})
                            </Button>
                            <Button
                                type={activeTab === 'products' ? "primary" : "text"}
                                onClick={() => setActiveTab('products')}
                                style={{ background: activeTab === 'products' ? 'var(--nearzon-purple)' : 'transparent', color: activeTab === 'products' ? '#fff' : '#64748b', borderRadius: '6px', fontWeight: 600, border: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                                <IconBox size={16} /> Products ({totalProducts})
                            </Button>
                            <Button
                                type={activeTab === 'shops' ? "primary" : "text"}
                                onClick={() => setActiveTab('shops')}
                                style={{ background: activeTab === 'shops' ? 'var(--nearzon-purple)' : 'transparent', color: activeTab === 'shops' ? '#fff' : '#64748b', borderRadius: '6px', fontWeight: 600, border: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                                <IconBuildingStore size={16} /> Shops ({shops.length})
                            </Button>
                        </div>

                        <div className="sr-filter-group">
                            <Dropdown menu={{
                                items: [
                                    { key: 'relevance', label: 'Relevance', onClick: () => updateFilter('sort', 'relevance') },
                                    { key: 'price-asc', label: 'Price: Low to High', onClick: () => updateFilter('sort', 'price-asc') },
                                    { key: 'price-desc', label: 'Price: High to Low', onClick: () => updateFilter('sort', 'price-desc') },
                                ]
                            }} trigger={['click']}>
                                <Button className={`sr-dropdown-btn ${debouncedFilters.sort && debouncedFilters.sort !== 'relevance' ? 'active-filter' : ''}`}>
                                    {debouncedFilters.sort === 'price-asc' ? 'Price: Low to High' : debouncedFilters.sort === 'price-desc' ? 'Price: High to Low' : 'Sort'} <IconChevronDown size={14} />
                                </Button>
                            </Dropdown>

                            <Dropdown menu={{
                                items: [
                                    { key: 'all', label: 'All Ratings', onClick: () => updateFilter('rating', 0) },
                                    { key: '4', label: '4 Stars & Up', onClick: () => updateFilter('rating', 4) },
                                    { key: '3', label: '3 Stars & Up', onClick: () => updateFilter('rating', 3) },
                                ]
                            }} trigger={['click']}>
                                <Button className={`sr-dropdown-btn ${debouncedFilters.rating > 0 ? 'active-filter' : ''}`}>
                                    {debouncedFilters.rating > 0 ? `${debouncedFilters.rating} Stars & Up` : 'Rating'} <IconChevronDown size={14} />
                                </Button>
                            </Dropdown>

                            <Dropdown menu={{
                                items: [
                                    { key: 'all', label: 'All Categories', onClick: () => updateFilter('categories', []) },
                                    ...categories.map(c => ({ key: c.id, label: c.name, onClick: () => updateFilter('categories', [c.id]) }))
                                ]
                            }} trigger={['click']}>
                                <Button className={`sr-dropdown-btn ${debouncedFilters.categories.length > 0 ? 'active-filter' : ''}`}>
                                    {debouncedFilters.categories.length === 1 ? (categories.find(c => c.id === debouncedFilters.categories[0])?.name || 'Category') : debouncedFilters.categories.length > 1 ? `Categories (${debouncedFilters.categories.length})` : 'Category'} <IconChevronDown size={14} />
                                </Button>
                            </Dropdown>

                            <Button type="text" onClick={handleReset} style={{ color: 'var(--nearzon-purple)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <IconFilterOff size={16} /> Clear Filters
                            </Button>
                        </div>
                    </div>

                    <Row gutter={[24, 24]}>
                        {/* Left Sidebar - Shops */}
                        {(activeTab === 'all' || activeTab === 'shops') && (
                            <Col xs={24} lg={activeTab === 'shops' ? 24 : 7} xl={activeTab === 'shops' ? 24 : 7}>
                                <div className="pcu-padding-24">
                                    <div className="sr-sidebar-title">
                                        Shops ({shops.length})
                                        {shops.length > 4 && <span>View all shops &rarr;</span>}
                                    </div>

                                    {loading ? (
                                        <div className="sr-sidebar-shops">
                                            {[...Array(3)].map((_, index) => (
                                                <ShopCardSkeleton key={index} />
                                            ))}
                                        </div>
                                    ) : shops.length > 0 ? (
                                        <div className="sr-sidebar-shops">
                                            {shops.slice(0, 4).map((shop) => {
                                                const isOpen = shopStatuses[shop.id] !== undefined
                                                    ? shopStatuses[shop.id]
                                                    : (shop.business?.isOpen !== false);

                                                const status = {
                                                    isOpen,
                                                    reopenMessage: shop.business?.closedReason || "Closed"
                                                };

                                                return (
                                                    <div
                                                        key={shop.id}
                                                        className={`shop-card-horizontal ${!status.isOpen ? 'pcu-opacity-60' : ''}`}
                                                        onClick={() => status.isOpen && navigate(`/products/${shop.id}`)}
                                                        style={{ cursor: status.isOpen ? 'pointer' : 'not-allowed', position: 'relative', overflow: 'hidden' }}
                                                    >
                                                        {!status.isOpen && (
                                                            <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, background: 'rgba(255, 255, 255, 0.6)', backdropFilter: 'blur(1px)', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                                                                <div style={{ background: '#dc2626', color: 'white', fontSize: '13px', padding: '4px 16px', textAlign: 'center', fontWeight: 'bold', borderRadius: '4px', marginBottom: '8px' }}>
                                                                    Closed
                                                                </div>
                                                                {(shop.business?.reopenTimeUnknown || shop.business?.closedUntil || shop.business?.openingTime) && (
                                                                    <div style={{ color: 'black', fontSize: '14px', fontWeight: 'bold', textAlign: 'center' }}>
                                                                        {shop.business?.reopenTimeUnknown
                                                                            ? 'Temporarily Closed'
                                                                            : shop.business?.closedUntil
                                                                                ? `Opens ${getRelativeCloseText(shop.business.closedUntil)}`
                                                                                : `Opens at ${formatTimeStr(shop.business.openingTime)}`
                                                                        }
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        <div className="shop-card-horizontal-img">
                                                            <SafeImage
                                                                alt={renderSafeText(shop.name)}
                                                                src={shop.imageUrl}
                                                                placeholderText="Shop"
                                                            />
                                                        </div>
                                                        <div className="shop-card-horizontal-info">
                                                            <div className="shop-card-horizontal-header-row">
                                                                <Title level={5} className="shop-horizontal-title">{renderSafeText(shop.name)}</Title>
                                                            </div>

                                                            <Text type="secondary" className="shop-horizontal-desc">
                                                                {renderSafeText(shop.description || 'Fresh products daily from your local store')?.substring(0, 65)}...
                                                            </Text>

                                                            <div className="shop-card-horizontal-footer">
                                                                <div className="footer-meta">
                                                                    {formatETA(shop.id, shop.estimatedDeliveryTime || shop.deliveryTime || shop.business?.deliveryTime || "30-45 mins") && (
                                                                        <div className="delivery-eta-pill">
                                                                            <IconTruck size={12} />
                                                                            <span>{formatETA(shop.id, shop.estimatedDeliveryTime || shop.deliveryTime || shop.business?.deliveryTime || "30-45 mins")}</span>
                                                                        </div>
                                                                    )}
                                                                    <div className="shop-rating-badge-mini mobile-hide">
                                                                        <span className="star-icon">★</span>
                                                                        <span className="rating-value">{shop.averageRating && shop.averageRating > 0 ? shop.averageRating.toFixed(1) : 'New'}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                        </div>
                                    ) : (
                                        <Empty description="No matching shops found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                    )}
                                </div>
                            </Col>
                        )}

                        {/* Right Content - Products */}
                        {(activeTab === 'all' || activeTab === 'products') && (
                            <Col xs={24} lg={activeTab === 'products' ? 24 : 17} xl={activeTab === 'products' ? 24 : 17}>
                                {/* Products Section */}
                                <div className={`sr-content-area pcu-padding-24 ${viewType === 'grid' ? 'sr-grid-view' : ''}`}>
                                    <div className="sr-sidebar-title">
                                        Products ({totalProducts})
                                        {totalProducts > 8 && (
                                            <span
                                                style={{ cursor: 'pointer', color: 'var(--nearzon-purple)' }}
                                                onClick={() => {
                                                    const queryParams = new URLSearchParams();
                                                    if (debouncedFilters.search) queryParams.set("search", debouncedFilters.search);
                                                    if (debouncedFilters.categories?.length > 0) {
                                                        debouncedFilters.categories.forEach(cat => queryParams.append("category", cat));
                                                    }
                                                    if (debouncedFilters.brands?.length > 0) {
                                                        debouncedFilters.brands.forEach(brand => queryParams.append("brand", brand));
                                                    }
                                                    navigate(`/all-products?${queryParams.toString()}`);
                                                }}
                                            >
                                                View all products &rarr;
                                            </span>
                                        )}
                                    </div>
                                    <RegularProductListing viewType={viewType} filters={debouncedFilters} hideTitle={true} onDataLoaded={setTotalProducts} hideShowMore={true} gridSpan={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 6 }} />
                                </div>
                            </Col>
                        )}
                    </Row>
                </div>

                <Drawer
                    title={
                        <div className="pcu-flex pcu-justify-between pcu-flex-center-y">
                            <span>Filters</span>
                            <Button type="link" onClick={handleReset} size="small" className="pcu-color-orange">Reset All</Button>
                        </div>
                    }
                    placement="left"
                    onClose={() => setMobileFiltersOpen(false)}
                    open={mobileFiltersOpen}
                    width={320}
                    styles={{ body: { padding: 0 } }}
                    footer={
                        <div style={{ textAlign: 'right', padding: '10px 16px' }}>
                            <Button
                                type="primary"
                                onClick={() => setMobileFiltersOpen(false)}
                                className="pcu-bg-purple pcu-border-none"
                                block
                            >
                                Apply Filters
                            </Button>
                        </div>
                    }
                >
                    <div className="pcu-padding-16">
                        <GlobalFilterSidebar
                            filters={filters}
                            onFilterChange={updateFilter}
                            onReset={handleReset}
                            categories={categories.map(c => ({ id: c._id || c.id, name: c.name, parentId: c.parentId?._id || c.parentId }))}
                            brands={brands.map(b => ({ id: b._id || b.id, name: b.name }))}
                            config={{
                                showSearch: true,
                                showZipcode: false,
                                showCategories: true,
                                showBrands: true,
                                showRating: true,
                            }}
                            style={{ boxShadow: 'none', padding: 0 }}
                            className="pcu-shadow-none pcu-padding-0"
                        />
                    </div>
                </Drawer>
            </div>
            <Footer />
        </>
    );
};

export default SearchResults;
